import { Link } from '@tanstack/react-router';
import { flexRender } from '@tanstack/react-table';

import { usePhoneWidth } from '@/shared/hooks/usePhoneWidth';

import {
  TASK_GRID_CLASS,
  TASK_GRID_STYLE,
  cellClass,
  type TaskTableRow,
} from '../taskColumns';
import { PhoneTaskSummary } from './TaskCells';

export interface TaskRowProps {
  workspaceId: string;
  row: TaskTableRow;
  /** From `taskRowHeight(density)` — the number the virtualiser measures by. */
  height: number;
}

/**
 * One task, anywhere in the workspace, as a row of the Tasks table.
 *
 * What each cell says is the column's business (`taskColumns.tsx`, spec 110);
 * this component owns what the *row* is: its height, its link and its layout.
 *
 * **The whole row is the link**, not a link inside it — the target 066
 * established for the same kind of row. A link occupying a fraction of a tall
 * row is a row most clicks miss.
 *
 * **It opens the task here** (spec 111, superseding 068 FR-007): the link adds
 * `task` and `tab` to the Tasks page's own address, keeping whatever the list
 * is narrowed to, and the page shows the drawer over the list. It stays a real
 * link, so opening a task in a new tab still works. Every row links — a task on
 * a draft workflow too: it has no board, but the drawer needs none.
 *
 * The height comes from the caller — `taskRowHeight(density)`, cozy or compact
 * since spec 114 — and is fixed because the virtualiser measures against it, and because
 * rows that resize as data arrives make a long list impossible to read while it
 * loads. Every cell is at most two lines and truncates rather than wrapping.
 */
export function TaskRow({ workspaceId, row: tableRow, height }: TaskRowProps) {
  const row = tableRow.original;
  // A failure is recorded on a run, so a failed row opens where the runs are —
  // the same rule Overview follows, rather than a second opinion about where a
  // broken task is best read.
  const tab = row.kind === 'failed-run' ? 'executions' : 'details';
  const phone = usePhoneWidth();

  // A phone has no room for the table's tracks (spec 265): the same row, same
  // height and link, drawn as two lines in which the title has the width.
  const updated = phone
    ? tableRow.getAllCells().find((cell) => cell.column.id === 'updated')
    : undefined;
  const updatedContext = updated?.getContext();
  const body = phone ? (
    <div
      className="flex w-full flex-col justify-center px-4"
      style={{ height }}
    >
      <PhoneTaskSummary
        row={row}
        density={updatedContext?.table.options.meta?.density}
        updated={
          updated && updatedContext
            ? flexRender(updated.column.columnDef.cell, updatedContext)
            : null
        }
      />
    </div>
  ) : (
    /*
      A grid whose tracks come from the column definitions, the same template
      the heading row uses — so every row has the same shape whatever it holds,
      and the state can be scanned down the list.
    */
    <div
      className={`${TASK_GRID_CLASS} w-full items-center text-xs`}
      style={{ ...TASK_GRID_STYLE, height }}
    >
      {tableRow.getAllCells().map((cell) => {
        const meta = cell.column.columnDef.meta;
        return (
          <div
            key={cell.id}
            data-column={cell.column.id}
            className={`${cellClass(meta)} ${meta?.align === 'end' ? 'text-muted-foreground tabular-nums' : ''}`}
          >
            {meta?.srLabel ? (
              <span className="sr-only">{meta.srLabel} </span>
            ) : null}
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </div>
        );
      })}
    </div>
  );

  return (
    <Link
      from="/workspaces/$workspaceId/tasks"
      to="/workspaces/$workspaceId/tasks"
      params={{ workspaceId }}
      search={(prev) => ({
        ...prev,
        task: row.taskId,
        tab,
        // An open preview belongs to the task it was opened from (spec 140
        // FR-064). This row is the Tasks page's open-task write, so it carries
        // the same rule the board and the run page do: a different task leaves
        // the previous task's previewed file behind, rather than beside a task
        // that never named it.
        preview: prev.task === row.taskId ? prev.preview : undefined,
      })}
      data-testid="task-row"
      data-task-id={row.taskId}
      // Hidden, and listed only because the reader asked (spec 112): dashed,
      // the way the board draws a hidden card. Only the title is dimmed (spec
      // 113 FR-009) — fading the whole row took its second line below AA in
      // the dark theme.
      data-hidden={row.hidden ? 'true' : undefined}
      className={`border-border/60 hover:bg-accent/50 focus-visible:ring-ring block border-b transition-colors focus-visible:ring-3 focus-visible:outline-none ${
        row.hidden
          ? 'outline-1 outline-offset-[-1px] outline-muted-foreground/40 outline-dashed'
          : ''
      }`}
    >
      {body}
    </Link>
  );
}
